// ============================================================
// LiveAnnouncer.jsx — the platform's single pair of ARIA live regions.
//
// WCAG 2.1 AA 4.1.3 (Status Messages): a status message must be
// programmatically determinable by assistive technology WITHOUT
// receiving focus. Loading states, error states, "no results", save
// confirmations and truncation notices are all status messages.
//
// WHY A PERSISTENT REGION RATHER THAN role="status" ON EACH MESSAGE
// ------------------------------------------------------------------
// The obvious approach — put role="status" on the message element —
// is unreliable in practice, and the reason is subtle. A React status
// message is CONDITIONALLY RENDERED:
//
//     {loading && <div role="status">Loading appraisals…</div>}
//
// so the live region is inserted into the DOM *already carrying its
// text*. Screen readers watch a live region for MUTATIONS; a region
// that did not exist a moment ago has no "before" state to diff
// against, and several SR/browser pairings announce nothing at all.
// The reliable pattern is a region that is ALWAYS mounted and EMPTY,
// whose text content is then changed.
//
// So this component mounts once, at the app root, and stays empty
// until something calls announce(). StatusMessage (and Toast) keep
// their inline role/aria-live as well — that costs nothing and serves
// the SRs that DO announce inserted regions. Belt and braces: the
// inline role is the auditable artefact, the persistent region is the
// guarantee.
//
// IDENTICAL CONSECUTIVE MESSAGES
// ------------------------------------------------------------------
// Setting a live region to the text it already holds is not a mutation,
// so nothing is announced — but "Couldn't load." twice in a row is two
// real events the user needs to hear. Each region therefore holds TWO
// child nodes and the text alternates between them, which guarantees a
// DOM mutation even when the string is unchanged.
//
// POLITENESS
// ------------------------------------------------------------------
// 'polite'    — waits for a pause. Loading, empty results, saved.
// 'assertive' — interrupts. Errors and anything the user must act on.
// Assertive is deliberately rare: an interrupting region that fires on
// routine state changes trains people to ignore it.
//
// announce() is a plain module function, not a hook or context value,
// so it is callable from portals (Toast renders into document.body),
// from effects, and from non-component code without prop-drilling a
// provider through every panel on the platform.
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';

// Queue anything announced before the component has mounted (a fetch
// that rejects during the very first render pass) and flush on mount,
// so an early error is never silently dropped.
let emit = null;
const preMountQueue = [];

// How long a message stays in the region before it is cleared. Long
// enough for any SR to pick it up, short enough that a user browsing
// the page later does not meet a stale "Loading…" sitting in the
// accessibility tree.
const CLEAR_MS = 7000;

/**
 * Announce a status message to assistive technology.
 * @param {string} message
 * @param {'polite'|'assertive'} politeness
 */
export function announce(message, politeness = 'polite') {
  if (!message || typeof message !== 'string') return;
  const trimmed = message.trim();
  if (!trimmed) return;
  const level = politeness === 'assertive' ? 'assertive' : 'polite';
  if (emit) emit(trimmed, level);
  else preMountQueue.push([trimmed, level]);
}

// Test seam: drop any queued-but-unflushed messages between tests.
export function __resetAnnouncerForTests() {
  emit = null;
  preMountQueue.length = 0;
}

export default function LiveAnnouncer() {
  // slot alternates 0/1 so identical consecutive text still mutates.
  const [polite, setPolite] = useState({ text: '', slot: 0 });
  const [assertive, setAssertive] = useState({ text: '', slot: 0 });

  const push = useCallback((text, level) => {
    const setter = level === 'assertive' ? setAssertive : setPolite;
    setter((prev) => ({ text, slot: prev.slot === 0 ? 1 : 0 }));
  }, []);

  useEffect(() => {
    emit = push;
    while (preMountQueue.length) {
      const [text, level] = preMountQueue.shift();
      push(text, level);
    }
    return () => { emit = null; };
  }, [push]);

  // Clear each region a while after it last changed. Two separate
  // effects so a polite message does not reset the assertive timer.
  useEffect(() => {
    if (!polite.text) return undefined;
    const t = setTimeout(() => setPolite((p) => ({ ...p, text: '' })), CLEAR_MS);
    return () => clearTimeout(t);
  }, [polite.text, polite.slot]);

  useEffect(() => {
    if (!assertive.text) return undefined;
    const t = setTimeout(() => setAssertive((p) => ({ ...p, text: '' })), CLEAR_MS);
    return () => clearTimeout(t);
  }, [assertive.text, assertive.slot]);

  return (
    <>
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="live-region-polite"
      >
        <div>{polite.slot === 0 ? polite.text : ''}</div>
        <div>{polite.slot === 1 ? polite.text : ''}</div>
      </div>
      <div
        className="sr-only"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        data-testid="live-region-assertive"
      >
        <div>{assertive.slot === 0 ? assertive.text : ''}</div>
        <div>{assertive.slot === 1 ? assertive.text : ''}</div>
      </div>
    </>
  );
}
